// App.js
import React, { useState, useEffect, useSyncExternalStore, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import apiClient from './config/api';
import { clearCurrentProfile, loadCurrentProfile, getCurrentProfile, subscribeCurrentProfile } from './services/currentProfile';

// Shared document styles keep a consistent order across lazy routes.
import './styles/OwnerDetail.css';
import './styles/cao.css';
import 'react-pdf/dist/Page/TextLayer.css';

import LoginForm from './pages/Login';
import Welcome from './pages/Welcome';
import LandingPage from './pages/LandingPage';             // NEW
import Footer from './components/Footer';

const RegisterForm = lazy(() => import('./pages/Register'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const Home = lazy(() => import('./pages/Home'));
const AddNew = lazy(() => import('./pages/AddNew'));
const RecycleBin = lazy(() => import('./components/RecycleBin'));
const IndustrialOwners = lazy(() => import('./pages/IndustrialOwners'));
const CaoPage = lazy(() => import('./pages/cao'));
const Admin = lazy(() => import('./pages/Admin'));
const Contracts = lazy(() => import('./pages/Contracts'));
const Protests = lazy(() => import('./pages/Protests'));
const ConfirmAccount = lazy(() => import('./pages/ConfirmAccount'));
const Tools = lazy(() => import('./pages/Tools'));
const AttachmentManager = lazy(() => import('./pages/AttachmentManager'));
const RestrictedUsers = lazy(() => import('./pages/RestrictedUsers'));
const Tickets = lazy(() => import('./pages/Tickets'));
const CreateTicket = lazy(() => import('./pages/CreateTicket'));
const TicketDetail = lazy(() => import('./pages/TicketDetail'));

const App = () => {
  const [loggedIn, setLoggedIn] = useState(false);
  const profile = useSyncExternalStore(subscribeCurrentProfile, getCurrentProfile, getCurrentProfile);
  const isAdmin = [true, 1, '1'].includes(profile?.isAdmin);
  const userId = profile?.userID || null;
  const [authReady, setAuthReady] = useState(false);

  const handleLogin = async () => {
    clearCurrentProfile();
    setLoggedIn(true);
    try { const current = await loadCurrentProfile(); setLoggedIn(Boolean(current?.username)); } catch { clearCurrentProfile(); setLoggedIn(false); }
  };

  const handleLogout = async () => {
    try {
      await apiClient.post('/auth/logout', {});
      clearCurrentProfile();
      setLoggedIn(false);
    } catch (err) {
      console.error('Logout failed:', err);
    }
  };

  useEffect(() => {
    let live = true;
    loadCurrentProfile()
      .then(data => {
        if (!live) return;
        setLoggedIn(Boolean(data?.username));
      })
      .catch(() => { if (live) { clearCurrentProfile(); setLoggedIn(false); } })
      .finally(() => { if (live) setAuthReady(true); });

      const interceptor = apiClient.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error?.response?.status === 401) {
          clearCurrentProfile();
          setLoggedIn(false);
        }
        return Promise.reject(error);
      }
    );

    // cleanup on unmount
    return () => {
      live = false;
      apiClient.interceptors.response.eject(interceptor);
    };
  }, []);

  

  

  if (!authReady) return null;

  return (
    <Router>
      <Suspense fallback={<div className="ticket-empty" role="status">Loading page…</div>}>
      <AppRoutes
        loggedIn={loggedIn}
        isAdmin={isAdmin}
        userId={userId}
        onLogin={handleLogin}
        onLogout={handleLogout}
      />
      </Suspense>
    </Router>
  );
};


const allowedRestrictedEditors = [70,74,75,80,100];
const allowedDomainEditors = [70,74,72,75,80,100];
const allowedCategoryEditors = allowedDomainEditors;

const AppRoutes = ({ loggedIn, isAdmin, userId, onLogin, onLogout }) => {
  const location = useLocation();
  const isLandingPage = location.pathname === '/landing';
  const PageLayout = isLandingPage ? 'div' : React.Fragment;
  const hideFooter = ['/login', '/welcome', '/confirm', '/forgot-password', '/reset-password'].includes(location.pathname);
  const canEditRestricted = allowedRestrictedEditors.includes(Number(userId));
  const canEditDomains = allowedDomainEditors.includes(Number(userId));
  const canEditCategories = allowedCategoryEditors.includes(Number(userId));

  return (
    <PageLayout {...(isLandingPage ? { className: 'app-landing-layout' } : {})}>
      <Routes>
        {/* First stop after login: Welcome (auto-redirects to /landing) */}
        <Route
          path="/"
          element={loggedIn ? <Navigate to="/welcome" replace /> : <Navigate to="/login" replace />}
        />
        <Route
          path="/welcome"
          element={loggedIn ? <Welcome /> : <Navigate to="/login" replace />}
        />
        <Route
          path="/landing"
          element={
            loggedIn ? (
              <LandingPage
                onLogout={onLogout}
                isAdmin={isAdmin}
                userId={userId}
                canEditRestricted={canEditRestricted}
                canEditDomains={canEditDomains}
                canEditCategories={canEditCategories}
              />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />

        <Route path="/dashboard" element={<Navigate to={loggedIn ? '/landing' : '/login'} replace />} />
        <Route path="/contracts" element={loggedIn ? <Contracts onLogout={onLogout} /> : <Navigate to="/login" replace />} />
        <Route path="/protests" element={loggedIn ? <Protests onLogout={onLogout} /> : <Navigate to="/login" replace />} />
        <Route path="/admin" element={!loggedIn ? <Navigate to="/login" replace /> : isAdmin ? <Admin onLogout={onLogout} isAdmin={isAdmin} canEditDomains={canEditDomains} canEditCategories={canEditCategories} canEditRestricted={canEditRestricted} /> : <Navigate to="/landing" replace />} />

        {/* Main pages */}
        <Route
          path="/home"
          element={loggedIn ? <Home onLogout={onLogout} /> : <Navigate to="/login" replace />}
        />
        <Route
          path="/owner-details"
          element={
            loggedIn ? (
              <IndustrialOwners onLogout={onLogout} />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/cao"
          element={loggedIn ? <CaoPage onLogout={onLogout} /> : <Navigate to="/login" replace />}
        />
        <Route
          path="/tools"
          element={
            loggedIn ? (
              (isAdmin && canEditDomains) ? <Tools onLogout={onLogout} isAdmin={isAdmin} /> : <Navigate to="/landing" replace />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/tools/attachments"
          element={
            loggedIn ? (
              (isAdmin && canEditCategories) ? <AttachmentManager onLogout={onLogout} isAdmin={isAdmin} /> : <Navigate to="/landing" replace />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/tools/restricted"
          element={
            loggedIn ? (
              (isAdmin && canEditRestricted) ? <RestrictedUsers onLogout={onLogout} isAdmin={isAdmin} /> : <Navigate to="/landing" replace />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/add"
          element={loggedIn ? <AddNew onLogout={onLogout} /> : <Navigate to="/login" replace />}
        />
        <Route
          path="/tickets"
          element={loggedIn ? <Tickets onLogout={onLogout} /> : <Navigate to="/login" replace />}
        />
        <Route
          path="/tickets/new"
          element={loggedIn ? <CreateTicket onLogout={onLogout} /> : <Navigate to="/login" replace />}
        />
        <Route
          path="/tickets/:ticketId"
          element={loggedIn ? <TicketDetail onLogout={onLogout} /> : <Navigate to="/login" replace />}
        />
        <Route
          path="/recyclebin"
          element={loggedIn ? <RecycleBin onLogout={onLogout} /> : <Navigate to="/login" replace />}
        />

        <Route
          path="/confirm"
          element={<ConfirmAccount />}
        />
        <Route
          path="/forgot-password"
          element={<ForgotPassword />}
        />
        <Route
          path="/reset-password"
          element={<ResetPassword />}
        />

        {/* Auth */}
        <Route
          path="/login"
          element={loggedIn ? <Navigate to="/welcome" replace /> : <AuthPages onLogin={onLogin} />}
        />
        <Route
          path="/register"
          element={<RegisterForm onRegistered={() => (window.location.href = '/login')} />}
        />
      </Routes>
      {loggedIn && !hideFooter && <Footer />}
    </PageLayout>
  );
};

const AuthPages = ({ onLogin }) => (
  <LoginForm onLogin={onLogin} />
);

export default App;





















// import React, { useState, useEffect, useSyncExternalStore, lazy, Suspense } from 'react';
// import { BrowserRouter as Router, Routes, Route, Navigate, Link } from 'react-router-dom';
// import apiClient from './config/api';
// import LoginForm from './pages/Login';
// const RegisterForm = lazy(() => import('./pages/Register'));
// import FileUploadForm from './components/FileUploadForm';
// const RecycleBin = lazy(() => import('./components/RecycleBin'));

// const App = () => {
//   const [loggedIn, setLoggedIn] = useState(false);

//   const handleLogin = () => setLoggedIn(true);

//   const handleLogout = async () => {
//     try {
//       await axios.post('http://www.typortal.com/api/logout', {}, { withCredentials: true });
//       setLoggedIn(false);
//     } catch (err) {
//       console.error('Logout failed:', err);
//     }
//   };

//   useEffect(() => {
//     axios.get('http://www.typortal.com/api/session', { withCredentials: true })
//     .then(res => {
//       if (res.data.username) setLoggedIn(true);
//     })
//     .catch(err => {
//       if (err.response?.status !== 401) {
//         console.error('Failed to fetch session:', err);
//       }
//       setLoggedIn(false);
//     });
//   }, []);

//   return (
//     <Router>
//       <Routes>
//         <Route
//           path="/"
//           element={
//             loggedIn ? (
//               <>
//                 <FileUploadForm />
//                 {/* <div style={{ textAlign: 'center', marginTop: '20px' }}>
//                   <button onClick={handleLogout} style={styles.logoutButton}>Logout</button>
//                 </div> */}
//               </>
//             ) : (
//               <Navigate to="/login" replace />
//             )
//           }
//         />
//         <Route
//           path="/recyclebin"
//           element={
//             loggedIn ? (
//               <>
//                 <div style={styles.navWrapper}>
//                   <Link to="/">
//                     <button style={styles.backButton}>Back to Home</button>
//                   </Link>
//                   <button onClick={handleLogout} style={styles.logoutButton}>Logout</button>
//                 </div>
//                 <RecycleBin />
//               </>
//             ) : (
//               <Navigate to="/login" replace />
//             )
//           }
//         />
//         <Route
//           path="/login"
//           element={
//             loggedIn ? (
//               <Navigate to="/" replace />
//             ) : (
//               <>
//                 <LoginForm onLogin={handleLogin} />
//                 <p style={{ textAlign: 'center' }}>
//                   Don't have an account? <Link to="/register">Register</Link>
//                 </p>
//               </>
//             )
//           }
//         />
//         <Route
//           path="/register"
//           element={
//             <>
//               <RegisterForm onRegistered={() => window.location.href = '/login'} />
//               <p style={{ textAlign: 'center' }}>
//                 Already have an account? <Link to="/login">Back to Login</Link>
//               </p>
//             </>
//           }
//         />
//       </Routes>
//     </Router>
//   );
// };

// const styles = {
//   logoutButton: {
//     padding: '10px 20px',
//     fontSize: '16px',
//     backgroundColor: '#d32f2f',
//     color: '#fff',
//     border: 'none',
//     borderRadius: '5px',
//     cursor: 'pointer',
//     marginLeft: '10px'
//   },
//   backButton: {
//     background: 'linear-gradient(135deg, #d67b7b 0%, #c45a5a 100%)',
//     color: '#fff',
//     padding: '10px 20px',
//     border: 'none',
//     borderRadius: '8px',
//     cursor: 'pointer',
//     fontSize: '16px',
//     fontWeight: '500',
//     boxShadow: '0 8px 20px rgba(214,123,123,0.3)',
//     transition: 'all 0.3s ease',
//   },
//   navWrapper: {
//     display: 'flex',
//     justifyContent: 'space-between',
//     alignItems: 'center',
//     padding: '20px',
//     marginBottom: '-30px',
//     marginLeft:'50px',
//     marginRight:'50px',
//     //backgroundColor: '#f8f9fa',
//   },
// };

// export default App;











